import React, { useEffect, useMemo, useState } from "react";
import { NoTranslateTextarea } from "../NoTranslateTextarea";
import { ManualTranslationRequestForm } from "../manual-translation-request/ManualTranslationRequestForm";
import {
  Flag,
  LanguageCode,
  renderLanguageFromCode,
  SUICustomerLanguageDropdown,
} from "../../customers/customerlanguages";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { Dimmer, Icon } from "semantic-ui-react";
import { useGetAuthQuery } from "../../api/authApi";
import { useGetCustomerQuery } from "../../api/customerApi";
import { requestManualTranslation } from "../../api/requestTranslationApi";
import { Spinner } from "../../utils/Spinner";
import { RootState } from "../../utils/store";
import { Text } from "../../components/Text";
import { Button } from "../../components/tailwind";

export const ManualTranslationRequestText: React.FC = () => {
  const { data: customer, isLoading } = useGetCustomerQuery();
  const { data: auth, isLoading: isAuthLoading } = useGetAuthQuery();
  const token = useSelector((state: RootState) => state.auth.token);
  const [isRequesting, setIsRequesting] = useState(false);
  const [jobIds, setJobIds] = useState<{ [lang: string]: string }>({});
  const [email, setEmail] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [sourceText, setSourceText] = useState("");

  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<
    LanguageCode
  >();
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageCode[]>(
    []
  );

  const allLanguagesHasBeenSelected =
    selectedLanguages.length === customer?.languages.length - 1;

  useEffect(() => {
    if (selectedLanguages.includes(selectedSourceLanguage)) {
      setSelectedLanguages(
        [...selectedLanguages].filter((code) => code !== selectedSourceLanguage)
      );
    }
  }, [selectedSourceLanguage]);

  useEffect(() => {
    if (customer && customer.config.tag_input_language) {
      setSelectedSourceLanguage(customer.config.tag_input_language);
    }
  }, [customer]);

  const handleSelectLanguage = (code: LanguageCode): void => {
    if (code === selectedSourceLanguage) {
      return;
    }
    if (selectedLanguages.includes(code)) {
      setSelectedLanguages([...selectedLanguages].filter((c) => c !== code));
      return;
    }
    const copy = [...selectedLanguages];
    copy.push(code);
    setSelectedLanguages(copy);
  };

  const handleSelectAll = (): void => {
    if (allLanguagesHasBeenSelected) {
      setSelectedLanguages([]);
      return;
    }
    const languageCodesToSelect = customer.languages
      .filter(({ code }) => code !== selectedSourceLanguage)
      .map(({ code }) => code);
    setSelectedLanguages(languageCodesToSelect);
  };

  const handleRequestManualTranslationText = async (): Promise<void> => {
    try {
      const results = await Promise.all(
        selectedLanguages.map((language) => {
          return requestManualTranslation(
            token,
            email,
            selectedSourceLanguage,
            [language],
            isUrgent,
            null,
            additionalInstructions,
            null,
            sourceText
          ).then((jobId) => {
            return { language, jobId };
          });
        })
      );
      setJobIds((prevJobIds) => {
        const newJobIds = { ...prevJobIds };
        results.forEach(({ language, jobId }) => {
          newJobIds[language] = jobId;
        });
        return newJobIds;
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRequestManualTranslation = async (): Promise<void> => {
    if (!canSendRequest) return;
    setIsRequesting(true);

    await handleRequestManualTranslationText();
  };

  const resetAllStates = (): void => {
    setJobIds({});
    setIsUrgent(false);
    setAdditionalInstructions("");
    setSelectedSourceLanguage(null);
    setSelectedLanguages([]);
    setIsRequesting(false);
    setSourceText("");
  };

  useEffect(() => {
    if (isAuthLoading || !auth) return;
    setEmail(auth.user.email);
  }, [auth, isAuthLoading]);

  const canSendRequest = useMemo((): boolean => {
    return !!(
      selectedSourceLanguage &&
      email &&
      selectedLanguages.length &&
      sourceText
    );
  }, [selectedSourceLanguage, selectedLanguages, email, sourceText]);
  return (
    <>
      <div className="tw-grid tw-grid-cols-2 tw-gap-8">
        <div className="tw-mt-4">
          <SUICustomerLanguageDropdown
            loading={isLoading}
            clearable
            fluid
            placeholder="Select source language"
            selection
            value={selectedSourceLanguage}
            onChange={(e, { value }): void =>
              setSelectedSourceLanguage(value as LanguageCode)
            }
            data-testid="manual-translation-text-source-dropdown"
          />
          <div>
            <NoTranslateTextarea
              value={sourceText}
              onChange={setSourceText}
              testId="manual-translation-request-textarea"
              placeholder="Enter text to translate..."
            ></NoTranslateTextarea>
            <ManualTranslationRequestForm
              email={email}
              setEmail={setEmail}
              isUrgent={isUrgent}
              setIsUrgent={setIsUrgent}
              additionalInstructions={additionalInstructions}
              setAdditionalInstructions={setAdditionalInstructions}
            />
          </div>
        </div>
        <div>
          <table className="txu-table txu-compact txu-clickable-rows tw-mb-4">
            <thead>
              <tr>
                <th>Language</th>
                <th>Manual Translation</th>
              </tr>
            </thead>
            <tbody>
              {customer?.languages.map((language) => (
                <tr
                  data-testid={`manual-translation-request-table-row-${language.code}`}
                  key={language.code}
                  onClick={(): void => handleSelectLanguage(language.code)}
                >
                  <td>
                    <Flag content={language.flag} /> {language.short_name}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      className="txu-input"
                      data-testid={`manual-translation-request-table-row-${language.code}-checkbox`}
                      checked={selectedLanguages.includes(language.code)}
                      disabled={language.code === selectedSourceLanguage}
                      onChange={(): void => handleSelectLanguage(language.code)}
                    />
                  </td>
                </tr>
              ))}
              <tr onClick={(): void => handleSelectAll()}>
                <td>Select All</td>
                <td>
                  <input
                    type="checkbox"
                    checked={allLanguagesHasBeenSelected}
                    onChange={(): void => handleSelectAll()}
                    className="txu-input"
                    data-testid="manual-translation-text-select-all-button"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <Button
            variant="primary"
            content="Send request"
            className="tw-float-end"
            onClick={(): Promise<void> => handleRequestManualTranslation()}
            disabled={!canSendRequest}
            data-testid="manual-translation-text-send-button"
          />
        </div>
      </div>

      <Dimmer active={isRequesting || !isEmpty(jobIds)} page>
        {isRequesting ? (
          <Spinner
            inverted
            content="Sending for Manual translation..."
            size="medium"
          />
        ) : (
          <>
            <Icon name="check" />
            <Text size="large">Sent for Manual translation!</Text>
            <Text size="small">
              You will receive an email at <b>{email}</b> when the manual
              translation is complete.
            </Text>
            <ul>
              {Object.entries(jobIds).map(([languageCode, jobId]) => {
                return (
                  <li key={languageCode} className="tw-my-2">
                    <strong>
                      {renderLanguageFromCode(
                        languageCode,
                        customer?.languages ?? []
                      )}
                    </strong>
                    : {jobId}
                  </li>
                );
              })}
            </ul>
            <Text size="small">
              Save the job ID(s) for use in correspondence with us.
              <br />
              Any related questions can be sent to{" "}
              <a
                href="mailto:translations@textual.se"
                style={{ color: "white" }}
              >
                translations@textual.se
              </a>
            </Text>
            <hr className="tw-my-4 tw-border-gray-600" />
            <Button content="Done" variant="primary" onClick={resetAllStates} />
          </>
        )}
      </Dimmer>
    </>
  );
};
